// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

import { useState } from "react";
import { Button, Input, Modal, Radio, Space, Typography } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";

// Expiry options in seconds (0 = no expiry).
const EXPIRY_OPTIONS = [
  { value: 0, label: "No expiry" },
  { value: 300, label: "5 minutes" },
  { value: 3600, label: "1 hour" },
  { value: 21600, label: "6 hours" },
  { value: 86400, label: "1 day" },
];

// PollComposer is the modal for creating a poll: a question, 2+ options, and an
// optional expiry after which voting closes.
export function PollComposer({
  open,
  onClose,
  onCreate,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onCreate: (question: string, answers: string[], expirySeconds: number) => void;
}) {
  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState<string[]>(["", ""]);
  const [expiry, setExpiry] = useState(0);

  function reset() {
    setQuestion("");
    setOptions(["", ""]);
    setExpiry(0);
  }
  function setOption(i: number, v: string) {
    setOptions((prev) => prev.map((o, idx) => (idx === i ? v : o)));
  }
  function addOption() {
    setOptions((prev) => [...prev, ""]);
  }
  function removeOption(i: number) {
    setOptions((prev) => (prev.length <= 2 ? prev : prev.filter((_, idx) => idx !== i)));
  }

  const answers = options.map((o) => o.trim()).filter(Boolean);
  const valid = question.trim().length > 0 && answers.length >= 2;

  function create() {
    if (!valid) return;
    onCreate(question.trim(), answers, expiry);
    reset();
    onClose();
  }
  function cancel() {
    reset();
    onClose();
  }

  return (
    <Modal title="Create a poll" open={open} onOk={create} onCancel={cancel} okText="Create poll" okButtonProps={{ disabled: !valid }}>
      <Space direction="vertical" style={{ width: "100%" }} size="middle">
        <Input placeholder="Ask a question…" value={question} onChange={(e) => setQuestion(e.target.value)} autoFocus />
        <div className="poll-compose-options">
          {options.map((o, i) => (
            <div className="poll-compose-row" key={i}>
              <Input
                placeholder={`Option ${i + 1}`}
                value={o}
                onChange={(e) => setOption(i, e.target.value)}
                onPressEnter={i === options.length - 1 ? addOption : undefined}
              />
              <Button
                type="text"
                icon={<DeleteOutlined />}
                disabled={options.length <= 2}
                onClick={() => removeOption(i)}
                title="Remove option"
              />
            </div>
          ))}
          <Button type="dashed" icon={<PlusOutlined />} onClick={addOption} block>
            Add option
          </Button>
        </div>
        <div>
          <Typography.Text type="secondary">Closes after</Typography.Text>
          <Radio.Group
            style={{ display: "block", marginTop: 4 }}
            value={expiry}
            onChange={(e) => setExpiry(e.target.value)}
            optionType="button"
            buttonStyle="solid"
            options={EXPIRY_OPTIONS}
          />
        </div>
      </Space>
    </Modal>
  );
}
